import isEqual from 'fast-deep-equal';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';

import { AudienceStatus } from 'types/api/audience';
import { ISavePromptState, SavePromptState } from 'types/pages/audience-builder/shared/save-prompt';

const actionName = 'save-prompt';

const initialState: ISavePromptState = {
  applied: true,
  audienceId: null,
  description: null,
  giftDateEnd: null,
  giftDateStart: null,
  isAssignMailCodeEnabled: false,
  isControlsHidden: false,
  isMaster: false,
  isOpen: false,
  isOpenSave: false,
  isOpenSaveAndClose: false,
  isPreBuilt: false,
  isSaveAsMaster: false,
  isSaveAsPreBuilt: false,
  isSaveToAudience: false,
  mailCode: null,
  order: null,
  orderType: undefined,
  parameters: null,
  primarySelect: null,
  secondarySelect: null,
  shopId: null,
  statusId: AudienceStatus.Active,
  tags: [],
};

export const useSavePromptStore = createWithEqualityFn<SavePromptState>()(
  immer(
    devtools((set, _) => ({
      ...initialState,
      reset: () =>
        set({ ...initialState }, false, {
          type: `${actionName}/reset`,
        }),
      setApplied: (value) =>
        set({ applied: value }, false, {
          type: `${actionName}/setApplied`,
        }),
      setAudience: (value) =>
        set({ audience: value }, false, {
          type: `${actionName}/setAudience`,
        }),
      setAudienceId: (value) =>
        set({ audienceId: value }, false, {
          type: `${actionName}/setAudienceId`,
        }),
      setClient: (value) =>
        set({ client: value }, false, {
          type: `${actionName}/setClient`,
        }),
      setDescription: (value) =>
        set({ description: value }, false, {
          type: `${actionName}/setDescription`,
        }),
      setGiftDateEnd: (value) =>
        set({ giftDateEnd: value }, false, {
          type: `${actionName}/setGiftDateEnd`,
        }),
      setGiftDateStart: (value) =>
        set({ giftDateStart: value }, false, {
          type: `${actionName}/setGiftDateStart`,
        }),
      setIsAssignMailCodeEnabled: (value) =>
        set({ isAssignMailCodeEnabled: value }, false, {
          type: `${actionName}/setIsAssignMailCodeEnabled`,
        }),
      setIsControlsHidden: (value) =>
        set({ isControlsHidden: value }, false, {
          type: `${actionName}/setIsControlsHidden`,
        }),
      setIsMaster: (value) =>
        set({ isMaster: value }, false, {
          type: `${actionName}/setIsMaster`,
        }),
      setIsOpen: (value) =>
        set({ isOpen: value }, false, {
          type: `${actionName}/setIsOpen`,
        }),
      setIsOpenSave: (value) =>
        set({ isOpenSave: value }, false, {
          type: `${actionName}/setIsOpenSave`,
        }),
      setIsOpenSaveAndClose: (value) =>
        set({ isOpenSaveAndClose: value }, false, {
          type: `${actionName}/setIsOpenSaveAndClose`,
        }),
      setIsPreBuilt: (value) =>
        set({ isPreBuilt: value }, false, {
          type: `${actionName}/setIsPreBuilt`,
        }),
      setIsSaveAsMaster: (value) =>
        set({ isSaveAsMaster: value }, false, {
          type: `${actionName}/setIsSaveAsMaster`,
        }),
      setIsSaveAsPreBuilt: (value) =>
        set({ isSaveAsPreBuilt: value }, false, {
          type: `${actionName}/setIsSaveAsPreBuilt`,
        }),
      setIsSaveToAudience: (value) =>
        set({ isSaveToAudience: value }, false, {
          type: `${actionName}/setIsSaveToAudience`,
        }),
      setMailCode: (value) =>
        set({ mailCode: value }, false, {
          type: `${actionName}/setMailCode`,
        }),
      setNameMode: (value) =>
        set({ nameMode: value }, false, {
          type: `${actionName}/setNameMode`,
        }),
      setOrder: (value) =>
        set({ order: value }, false, {
          type: `${actionName}/setOrder`,
        }),
      setOrderType: (value) =>
        set({ orderType: value }, false, {
          type: `${actionName}/setOrderType`,
        }),
      setParameterMode: (value) =>
        set({ parameterMode: value }, false, {
          type: `${actionName}/setParameterMode`,
        }),
      setParameters: (value) =>
        set({ parameters: value }, false, {
          type: `${actionName}/setParameters`,
        }),
      setPrimarySelect: (value) =>
        set({ primarySelect: value }, false, {
          type: `${actionName}/setPrimarySelect`,
        }),
      setSaveMode: (value) =>
        set({ saveMode: value }, false, {
          type: `${actionName}/setSaveMode`,
        }),
      setSecondarySelect: (value) =>
        set({ secondarySelect: value }, false, {
          type: `${actionName}/setSecondarySelect`,
        }),
      setShopId: (value) =>
        set({ shopId: value }, false, {
          type: `${actionName}/setShopId`,
        }),
      setStatusId: (value) =>
        set(() => ({ statusId: value }), false, {
          type: `${actionName}setStatusId`,
        }),
      setTags: (value) =>
        set({ tags: value }, false, {
          type: `${actionName}/setTags`,
        }),
    }))
  ),
  isEqual
);
